import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { trackEventMixpanel } from 'utils/mixpanel';
import {
    PPA_WHITESPACE_INCREMENT_INPUT_CHANGED,
    PPA_WHITESPACE_INCREMENT_INVALID_ENTERED,
    PPA_WHITESPACE_INCREMENT_SLIDER_CHANGED,
    PPA_WHITESPACE_TOGGLE_CHANGED,
} from 'utils/mixpanelEventNames';
import { getWhitespaceIncrementFormat } from './whitespaceMixpanel.helpers';
import type { computeWhitespaceGridMetrics } from './whitespaceMixpanel.helpers';

type GridMetrics = ReturnType<typeof computeWhitespaceGridMetrics>;

type UseWhitespaceIncrementParams = {
    yAxis: string;
    incrementValue: number;
    incrementConfig: { min: number; max: number; step: number; label: string };
    setIncrementValue: (value: number) => void;
    showWhitespaces: boolean;
    setShowWhitespaces: (value: boolean) => void;
    baseProps: Record<string, unknown>;
    gridMetrics: GridMetrics;
    markConfigRefreshStart: () => void;
};

const useWhitespaceIncrement = ({
    yAxis,
    incrementValue,
    incrementConfig,
    setIncrementValue,
    showWhitespaces,
    setShowWhitespaces,
    baseProps,
    gridMetrics,
    markConfigRefreshStart,
}: UseWhitespaceIncrementParams) => {
    const showDollarPrefix = useMemo(
        () => !['ounces', 'count'].includes(yAxis.trim().toLowerCase()),
        [yAxis],
    );
    const incrementFormat = useMemo(() => getWhitespaceIncrementFormat(yAxis), [yAxis]);

    const [localIncrementValue, setLocalIncrementValue] = useState(incrementValue);
    const previousIncrementValueRef = useRef(incrementValue);

    useEffect(() => {
        setLocalIncrementValue(incrementValue);
        previousIncrementValueRef.current = incrementValue;
    }, [incrementValue]);

    const debouncedSetIncrementValue = useMemo(
        () =>
            debounce((value: number, previousValue: number) => {
                markConfigRefreshStart();
                setIncrementValue(value);
                trackEventMixpanel(PPA_WHITESPACE_INCREMENT_SLIDER_CHANGED, {
                    y_axis: yAxis,
                    previous_increment_value: previousValue,
                    selected_increment_value: value,
                    increment_format: incrementFormat,
                    ...baseProps,
                });
                previousIncrementValueRef.current = value;
            }, 300),
        [baseProps, incrementFormat, markConfigRefreshStart, setIncrementValue, yAxis],
    );

    useEffect(() => () => debouncedSetIncrementValue.cancel(), [debouncedSetIncrementValue]);

    const handleIncrementChange = useCallback(
        (value: number | [number, number]) => {
            const nextValue = typeof value === 'number' ? value : localIncrementValue;
            setLocalIncrementValue(nextValue);
            debouncedSetIncrementValue(nextValue, previousIncrementValueRef.current);
        },
        [debouncedSetIncrementValue, localIncrementValue],
    );

    const handleIncrementInputChange = useCallback(
        (value: number | null) => {
            if (value == null) {
                return;
            }

            if (value < incrementConfig.min || value > incrementConfig.max) {
                trackEventMixpanel(PPA_WHITESPACE_INCREMENT_INVALID_ENTERED, {
                    y_axis: yAxis,
                    entered_increment_value: value,
                    validation_error: 'out_of_bounds',
                    min_allowed_increment: incrementConfig.min,
                    max_allowed_increment: incrementConfig.max,
                });
                return;
            }

            setLocalIncrementValue(value);
            markConfigRefreshStart();
            setIncrementValue(value);
            trackEventMixpanel(PPA_WHITESPACE_INCREMENT_INPUT_CHANGED, {
                y_axis: yAxis,
                previous_increment_value: previousIncrementValueRef.current,
                entered_increment_value: value,
                increment_format: incrementFormat,
                ...baseProps,
            });
            previousIncrementValueRef.current = value;
        },
        [baseProps, incrementConfig.max, incrementConfig.min, incrementFormat, markConfigRefreshStart, setIncrementValue, yAxis],
    );

    const handleIncrementInputParser = useCallback(
        (displayValue: string | undefined) => {
            const parsed = Number(displayValue?.replace(/[^\d.]/g, ''));
            if (Number.isNaN(parsed)) {
                trackEventMixpanel(PPA_WHITESPACE_INCREMENT_INVALID_ENTERED, {
                    y_axis: yAxis,
                    entered_increment_value: displayValue ?? '',
                    validation_error: 'invalid_number',
                    min_allowed_increment: incrementConfig.min,
                    max_allowed_increment: incrementConfig.max,
                });
                return incrementConfig.min as number;
            }
            return parsed;
        },
        [incrementConfig.max, incrementConfig.min, yAxis],
    );

    const handleShowWhitespacesChange = useCallback(
        (checked: boolean) => {
            trackEventMixpanel(PPA_WHITESPACE_TOGGLE_CHANGED, {
                previous_state: showWhitespaces,
                new_state: checked,
                opportunity_cell_count: gridMetrics.opportunity_cell_count,
                visible_cell_count: gridMetrics.visible_cell_count,
                ...baseProps,
            });
            setShowWhitespaces(checked);
        },
        [baseProps, gridMetrics.opportunity_cell_count, gridMetrics.visible_cell_count, setShowWhitespaces, showWhitespaces],
    );

    return {
        showDollarPrefix,
        localIncrementValue,
        handleIncrementChange,
        handleIncrementInputChange,
        handleIncrementInputParser,
        handleShowWhitespacesChange,
    };
};

export default useWhitespaceIncrement;
